import OptionalChip from "@/components/Common/forms/OptionalChip";
import CountrySelect from "@/components/ui/CountrySelect";
import FlagComponent from "@/components/ui/FlagComponent";
import {Input} from "@/components/ui/input";
import clsxm from "@/lib/clsxm";
import {type ComponentProps, type FC, type RefObject} from "react";
import {useTranslation} from "react-i18next";
import * as RPNInput from "react-phone-number-input";

const normalizeToE164 = (phone?: string) => {
  if (!phone) return undefined;
  const cleaned = phone.replace(/[^\d+]/g, "");
  if (!cleaned) return undefined;
  if (cleaned.startsWith("+")) return cleaned;

  return `+${cleaned}`;
};

type PhoneInputProps = Omit<ComponentProps<"input">, "onChange" | "value" | "ref"> &
  Omit<RPNInput.Props<typeof RPNInput.default>, "onChange" | "value"> & {
    containerClassName?: string;
    label?: string;
    labelClassName?: string;
    onChange?: (value: RPNInput.Value) => void;
    optional?: boolean;
    showLabel?: boolean;
    numberInputClassName?: string;
    buttonClassName?: string;
    error?: string;
    value?: string;
    id?: string;
    dataTestId?: string;
  };

const PhoneInput: FC<PhoneInputProps & {ref?: RefObject<any>}> = ({
  ref,
  className,
  containerClassName,
  label,
  labelClassName,
  onChange,
  optional,
  showLabel = true,
  numberInputClassName,
  buttonClassName,
  error,
  value,
  id,
  dataTestId,
  ...props
}) => {
  const {t} = useTranslation("common");

  return (
    <div className={containerClassName}>
      {showLabel && (
        <label
          htmlFor={id ?? "phone-number"}
          className={clsxm("text-neutral-grey-dark g flex items-center gap-2 text-sm font-medium", labelClassName)}>
          {optional && <OptionalChip />}
          {label ?? t("Phone number")}
        </label>
      )}
      <RPNInput.default
        ref={ref}
        className={clsxm("flex", className)}
        flagComponent={FlagComponent}
        countrySelectComponent={CountrySelect}
        countrySelectProps={{buttonClassName, disabled: props.disabled, error}}
        inputComponent={InputComponent}
        numberInputProps={{className: numberInputClassName, error, id, dataTestId}}
        smartCaret={false}
        international
        value={normalizeToE164(value)}
        /**
         * Handles the onChange event.
         *
         * react-phone-number-input might trigger the onChange event as undefined
         * when a valid phone number is not entered. To prevent this,
         * the value is coerced to an empty string.
         *
         * @param {E164Number | undefined} value - The entered value
         */
        onChange={value => onChange?.(value || ("" as RPNInput.Value))}
        {...props}
      />
      {!!error && (
        <span className="text-danger-medium-red mt-0.5 text-xs">{t(error as "incorrect_phone_number_length")}</span>
      )}
    </div>
  );
};
PhoneInput.displayName = "PhoneInput";

type InputComponentProps = ComponentProps<"input"> & {
  error?: boolean;
  id?: string;
  dataTestId?: string;
};

const InputComponent: FC<InputComponentProps & {ref?: RefObject<HTMLInputElement>}> = ({
  ref,
  className,
  error,
  id,
  dataTestId,
  ...props
}) => {
  const {t} = useTranslation("company_profile");

  return (
    <Input
      id={id}
      data-testid={dataTestId}
      className={clsxm(
        "placeholder:text-neutral-grey-light h-14 rounded-s-none rounded-e-lg focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 md:text-base",
        "disabled:bg-neutral-white-dark disabled:text-neutral-grey-dark disabled:opacity-100",
        error && "border-danger-medium-red focus:border-danger-dark-red",
        className,
      )}
      placeholder={t("phone_number_placeholder")}
      {...props}
      ref={ref}
    />
  );
};

export {PhoneInput};
